import { AlertCircle, Hourglass, PackageOpen, Unlink } from 'lucide-react';
import type { ReactNode } from 'react';
import { exportAttachment, formatKB, type HandoffState } from '../lib/runeHandoff';
import { templateLabel } from '../lib/templates';

/**
 * What arrived (or did not) from the HIVE rune that opened this page (/send?…&handoff=1,
 * lib/runeHandoff.ts). Everything from the rune is rendered as React text (escaped); nothing
 * is sent from here — the visitor still presses Send in the form.
 */
export function HandoffNotice({
  state,
  template,
  signedIn,
  passSignIn = false,
  attachment,
}: {
  state: HandoffState | null;
  /** The rune slug of the link (?template=). */
  template: string;
  signedIn: boolean;
  /** Ordinal Pass sign-in is offered: it leaves the page, so say what happens to the data. */
  passSignIn?: boolean;
  /** Signed in: is the export's file in the form (lib/runeHandoff.ts attachmentStatus)? */
  attachment?: 'attached' | 'skipped' | 'removed';
}) {
  const rune = templateLabel(template);
  const muted = { color: 'rgba(240,253,244,0.65)' };
  const accent = { color: '#34D399' };

  if (!state || state.status === 'waiting') {
    return (
      <Card icon={<Hourglass size={16} style={accent} />} testId="waiting">
        <div>Waiting for the data from the <span className="font-mono" style={accent}>{rune}</span> rune…</div>
        <div className="text-xs mt-1" style={muted}>Keep the rune tab open. Nothing is sent until you press Send.</div>
      </Card>
    );
  }

  if (state.status === 'received') {
    const p = state.payload;
    const file = exportAttachment(p);
    return (
      <Card icon={<PackageOpen size={16} style={accent} />} testId="received" strong>
        <div className="label mb-1" style={accent}>From the HIVE rune {rune} · {p.kind}</div>
        <div className="text-base font-semibold break-words" style={{ color: '#F0FDF4' }}>{p.title}</div>
        {p.summary
          ? <div className="text-sm mt-1 whitespace-pre-wrap break-words" style={{ color: 'rgba(240,253,244,0.75)' }}>{p.summary}</div>
          : <div className="text-sm mt-1" style={muted}>(no summary)</div>}
        <div className="text-xs mt-2 font-mono" style={muted}>
          {formatKB(state.dataBytes)} JSON · created {p.createdAt}
        </div>
        <div className="text-xs mt-2" style={muted} data-attachment={signedIn ? attachment || 'pending' : 'signed-out'}>
          Nothing has been sent.{' '}
          {!signedIn && <>Sign in to review it in the send form. It is kept only in this tab&apos;s memory, not saved in the browser.</>}
          {signedIn && attachment === 'attached' && <>It is attached below as <span className="font-mono" style={accent}>{file.name}</span>, with the {rune} template and the title as purpose. Check the form and press Send.</>}
          {signedIn && attachment === 'removed' && <>Its file <span className="font-mono" style={accent}>{file.name}</span> is no longer in the list below, so it will not be sent. To add it again, go back to the rune and press COLLECT again, or download the JSON there.</>}
          {signedIn && attachment === 'skipped' && <span style={{ color: '#FBBF24' }}>The send form could not take its file <span className="font-mono">{file.name}</span> ({formatKB(state.fileBytes)}): it is over the form&apos;s 5 MB limit, so it is not attached and will not be sent.</span>}
          {signedIn && !attachment && <>Check the form and press Send.</>}
        </div>
        {!signedIn && passSignIn && (
          <div className="text-xs mt-2" style={muted}>
            Signing in with Ordinal Pass leaves this page. If the data is not here when you come back, go back to the rune tab and press COLLECT again, or download the JSON there.
          </div>
        )}
      </Card>
    );
  }

  if (state.status === 'invalid') {
    return (
      <Card icon={<AlertCircle size={16} style={{ color: '#FBBF24' }} />} testId="invalid" warn>
        <div>The {rune} rune sent data Collect cannot accept: {state.message}</div>
        <div className="text-xs mt-1" style={muted}>Nothing was added. You can still upload a file yourself.</div>
      </Card>
    );
  }

  if (state.status === 'timeout') {
    return (
      <Card icon={<AlertCircle size={16} style={{ color: '#FBBF24' }} />} testId="timeout" warn>
        <div>No data has arrived from the {rune} rune yet.</div>
        <div className="text-xs mt-1" style={muted}>
          If the rune offers &quot;Atsisiųsti JSON&quot;, save that file and {signedIn ? 'drop it below' : 'add it in the send form after you sign in'}.
        </div>
      </Card>
    );
  }

  // no-opener
  return (
    <Card icon={<Unlink size={16} style={{ color: '#FBBF24' }} />} testId="no-opener" warn>
      <div>This page was not opened from a HIVE rune window, so no rune data can arrive here.</div>
      <div className="text-xs mt-1" style={muted}>
        Go back to the {rune} rune and press COLLECT again, or download the JSON there (&quot;Atsisiųsti JSON&quot;) and add it here.
      </div>
    </Card>
  );
}

function Card({
  icon,
  children,
  testId,
  strong = false,
  warn = false,
}: {
  icon: ReactNode;
  children: ReactNode;
  testId: string;
  strong?: boolean;
  warn?: boolean;
}) {
  return (
    <div
      className={`${strong ? 'glass-panel-strong' : 'glass-panel'} rounded-xl p-4 mb-4 flex items-start gap-3`}
      style={warn ? { borderColor: 'rgba(245,158,11,0.4)' } : strong ? { borderColor: 'rgba(16,185,129,0.55)' } : undefined}
      data-handoff={testId}
      role={warn ? 'alert' : 'status'}
    >
      <span className="flex-none mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0 text-sm" style={{ color: 'rgba(240,253,244,0.85)' }}>{children}</div>
    </div>
  );
}
